/**
 * Validation utilities for CreateAuditSessionDialog
 *
 * Extracted validation logic to reduce component complexity and improve reusability.
 * All dates are handled in UTC.
 */

import { DATE_VALIDATION_ERRORS, MAX_DATE_RANGE_DAYS } from '@core/utils';
import type { SampleFilters } from '@internal-audit/domain';

/** Maximum allowed date range span for audit sessions (days). */
const AUDIT_MAX_DATE_RANGE_DAYS = 29;

/** Maximum lookback period — start date cannot be more than this many days before today. */
const AUDIT_MAX_LOOKBACK_DAYS = MAX_DATE_RANGE_DAYS; // 90 days

/**
 * Get default start date (AUDIT_MAX_DATE_RANGE_DAYS ago in UTC)
 */
export const getDefaultStartDate = (): string => new Date(Date.now() - AUDIT_MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

/**
 * Get default end date (today in UTC)
 */
export const getDefaultEndDate = (): string => new Date().toISOString().split('T')[0];

/**
 * Get minimum allowed start date (AUDIT_MAX_LOOKBACK_DAYS ago from today in UTC)
 */
export const getMinStartDate = (): string => {
  const today = new Date();
  const minStartDate = new Date(today);

  minStartDate.setUTCDate(today.getUTCDate() - AUDIT_MAX_LOOKBACK_DAYS);

  return minStartDate.toISOString().split('T')[0];
};

/**
 * Get maximum allowed end date (today in UTC)
 */
export const getMaxEndDate = (): string => new Date().toISOString().split('T')[0];

/**
 * Get the maximum end date allowed for a given start date.
 * The end date cannot exceed startDate + AUDIT_MAX_DATE_RANGE_DAYS or today, whichever is earlier.
 */
export const getMaxEndDateForStart = (startDate: string): string => {
  if (!startDate) return getMaxEndDate();

  const start = new Date(`${startDate}T00:00:00Z`);
  const maxFromStart = new Date(start);

  maxFromStart.setUTCDate(start.getUTCDate() + AUDIT_MAX_DATE_RANGE_DAYS);

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const maxFromStartStr = maxFromStart.toISOString().split('T')[0];

  return maxFromStartStr < todayStr ? maxFromStartStr : todayStr;
};

/**
 * Get the minimum start date allowed for a given end date.
 * The start date cannot be earlier than endDate - AUDIT_MAX_DATE_RANGE_DAYS or the lookback limit, whichever is later.
 */
export const getMinStartDateForEnd = (endDate: string): string => {
  if (!endDate) return getMinStartDate();

  const end = new Date(`${endDate}T00:00:00Z`);
  const minFromEnd = new Date(end);

  minFromEnd.setUTCDate(end.getUTCDate() - AUDIT_MAX_DATE_RANGE_DAYS);

  const absoluteMin = getMinStartDate();
  const minFromEndStr = minFromEnd.toISOString().split('T')[0];

  return minFromEndStr > absoluteMin ? minFromEndStr : absoluteMin;
};

/**
 * Get initial empty filters
 */
export const getInitialFilters = (): SampleFilters => ({
  languages: [],
  topics: [],
  labels: [],
  labelSeverities: [],
  contentTypes: [],
  customValues: undefined,
  violatesPolicy: undefined,
});

/**
 * Validate date range for audit session (all dates treated as UTC)
 * - End date cannot be in the future (compared at day level)
 * - Start date cannot be earlier than 90 days ago (compared at day level)
 * - End date cannot be before start date
 * - Date range span cannot exceed AUDIT_MAX_DATE_RANGE_DAYS (29 days)
 */
export const validateDateRange = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return '';

  // Parse dates as UTC by appending time and 'Z'
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);

  // Use same min/max as UI for consistency
  const minStart = new Date(`${getMinStartDate()}T00:00:00Z`);
  const maxEnd = new Date(`${getMaxEndDate()}T00:00:00Z`);

  // Check if end date is in the future
  if (end > maxEnd) {
    return DATE_VALIDATION_ERRORS.END_DATE_FUTURE;
  }

  // Check if start date is earlier than minimum allowed (90 days ago)
  if (start < minStart) {
    return DATE_VALIDATION_ERRORS.START_DATE_TOO_OLD;
  }

  // Check if end date is before start date
  if (end < start) {
    return DATE_VALIDATION_ERRORS.END_BEFORE_START;
  }

  // Check if the date range span exceeds the maximum allowed (29 days)
  const diffMs = end.getTime() - start.getTime();
  const diffDays = diffMs / (24 * 60 * 60 * 1000);

  if (diffDays > AUDIT_MAX_DATE_RANGE_DAYS) {
    return `Date range cannot exceed ${AUDIT_MAX_DATE_RANGE_DAYS} days`;
  }

  return '';
};

/**
 * Validates a custom key:value input string.
 * Returns validation result matching MultiSelect's validateCustomOption signature.
 *
 * Extracted from CustomValuesFilter so it can be unit-tested independently
 * alongside the other dialog validation helpers.
 */
export const validateCustomValues = (input: string, existingValues: string[]): { isValid: boolean; key?: string; value?: string; error?: string } => {
  const trimmed = input.trim();

  // Must contain a colon separator
  if (!trimmed.includes(':')) {
    return { isValid: false, error: 'Enter as key:value (e.g., priority:normal)' };
  }

  const colonIndex = trimmed.indexOf(':');
  const key = trimmed.substring(0, colonIndex).trim();
  const value = trimmed.substring(colonIndex + 1).trim();

  // Both key and value must be non-empty
  if (!key) {
    return { isValid: false, error: 'Key cannot be empty' };
  }

  if (!value) {
    return { isValid: false, error: 'Value cannot be empty' };
  }

  // Check for duplicates
  if (existingValues.includes(`${key}:${value}`)) {
    return { isValid: false, error: 'This key:value pair already exists' };
  }

  return { isValid: true, key, value };
};

/**
 * Check if form is valid for submission
 */
export const isFormValid = (params: { sessionName: string; startDate: string; endDate: string; dateError: string }): boolean =>
  Boolean(params.sessionName.trim() && params.startDate && params.endDate && !params.dateError);
